import React, { Suspense, lazy } from 'react';
import { Button } from '@/components/ui/button';
import { AmbientLines } from '@/components/AmbientLines';
import { SectionSeam } from '@/components/SectionSeam';
import HeroLab from '@/pages/HeroLab';
import { Navigation } from '@/components/Navigation';
import { useIsMobile } from '@/hooks/use-mobile';
import { SimpleReveal } from '@/components/SimpleReveal';
import { SpotifyPhoneMockup } from '@/components/SpotifyPhoneMockup';
import { AnimatedBorderBadge } from '@/components/AnimatedBorderBadge';
import { Youtube, Music2, Instagram, Mail, ArrowRight, Check } from 'lucide-react';
import logo from '@/assets/damian-keyes-logo.png';
import founderImage from '@/assets/damian-keyes-founder.png';
import academyMockup from '@/assets/academy-mockup-transparent.png';
import releasePlanImage from '@/assets/20-day-release-plan-2026.png';
import hooksImage from '@/assets/100-hooks-blueprint.png';
import contentIdeasImage from '@/assets/365-content-ideas-showcase.jpg';

// Lazy load below-fold components
const GrowthProjectionChart = lazy(() => import('@/components/GrowthProjectionChart').then(module => ({
  default: module.GrowthProjectionChart
})));
const Footer = lazy(() => import('@/components/Footer'));
export default function OriginalHome() {
  const applyUrl = "https://a23j5fry30t.typeform.com/to/CzjaFT5z";
  const academyTrialUrl = "https://www.dk-mba.com"; // Update with actual trial URL
  const agencyLearnMoreUrl = "https://www.dk-artists.com";
  const isMobile = useIsMobile();
  // Backup route only: keep out of search engines.
  React.useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'robots';
    m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, []);
  return <div className="min-h-screen bg-hero-human max-md:bg-white">
      {isMobile ? <Navigation /> : <HeroLab embedded />}
      
      {/* Hero Section - LIGHT */}
      <section id="about" data-nav-theme="light" style={isMobile ? undefined : { marginTop: "-100svh" }} className="relative isolate z-20 md:rounded-t-[2rem] md:shadow-[0_-24px_60px_-20px_hsl(0_0%_0%/0.35)] py-16 sm:py-20 lg:py-32 xl:py-40 bg-hero-human max-md:bg-white">
        <AmbientLines />
        <SectionSeam label="01 About" />
        <div className="max-w-7xl mx-auto px-6">
          {/* Centered Header */}
          <div className="text-center mb-12 lg:mb-16">
            <SimpleReveal>
              <AnimatedBorderBadge text="Where Creativity Meets Science" className="mb-6 mt-4 sm:mt-0 inline-block" showPulsingDot={true} surfaceClassName="bg-hero-human max-md:bg-white" />
            </SimpleReveal>
            <SimpleReveal delay={60}>
              <h1 className="font-headline font-bold text-[hsl(var(--light-text))] text-[clamp(2rem,4.5vw,3.5rem)] lg:text-[clamp(2.5rem,5vw,4rem)] leading-[1.05] tracking-[-0.05em] mx-auto max-w-5xl">
                Get your music heard, build a <span className="font-semibold text-[hsl(var(--accent-a))]">genuine fanbase</span> and become an Entrepreneurial Musician
              </h1>
            </SimpleReveal>
          </div>

          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 lg:grid-rows-1 gap-12 lg:gap-20 items-start">
          {/* Left Column - Copy */}
          <SimpleReveal delay={120} className="h-full lg:row-start-1 lg:col-start-1">
            <div className="flex flex-col h-full">
              <div className="space-y-6 text-[0.9375rem] sm:text-[1.0625rem] lg:text-[1.125rem] text-[hsl(var(--light-text-muted))] leading-[1.55] max-w-[680px] mb-6">
                <p>
                  Damian is a leading music industry specialist and entrepreneur with expertise in digital marketing, fanbase growth and artist development in a global world.
                </p>
                <p>
                  He previously founded the world's largest music University (BIMM - valued at $300M), the largest online Music Business Academy (DK-MBA) and has over 200K+ subscribers on YouTube.
                </p>
              </div>

              {/* Damian's Photo - Mobile Only (appears after intro, before credentials) */}
              <SimpleReveal delay={100} className="lg:hidden mb-6">
                <img src={founderImage} alt="Damian Keyes" className="w-full max-w-lg mx-auto rounded-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl" />
              </SimpleReveal>
                
                {/* Credentials Section */}
                <div className="pt-6 border-t border-[hsl(var(--light-text)/0.1)]">
                  <p className="text-xs font-semibold text-[hsl(var(--light-text-muted))] mb-4 uppercase tracking-wider">
                    IN A NUTSHELL
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-[hsl(var(--light-text-muted))] leading-relaxed">
                    <div className="flex items-start gap-2">
                      <span className="text-[hsl(var(--accent-a))] mt-0.5 flex-shrink-0">✓</span>
                      <span>Co-Founded Europe's Largest Music University, BIMM</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[hsl(var(--accent-a))] mt-0.5 flex-shrink-0">✓</span>
                      <span>Co-Founded The World's Largest Online Music Business Academy</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[hsl(var(--accent-a))] mt-0.5 flex-shrink-0">✓</span>
                      <span>BIMM currently valued at $300M with 9,000+ students/year</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[hsl(var(--accent-a))] mt-0.5 flex-shrink-0">✓</span>
                      <span>30,000+ Artists in DK Music Business Academy</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[hsl(var(--accent-a))] mt-0.5 flex-shrink-0">✓</span>
                      <span>Over 230K+ YouTube Subscribers</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[hsl(var(--accent-a))] mt-0.5 flex-shrink-0">✓</span>
                      <span>Over 15M+ YouTube views</span>
                    </div>
                  </div>
                </div>

                {/* CTA Container */}
                <div className="mt-20 lg:mt-auto">
                  <div className="bg-[#0a0a0a] border border-[hsl(var(--accent-a))]/20 rounded-2xl p-6 sm:p-8 lg:p-10 space-y-4 sm:space-y-5">
                    <div className="space-y-2">
                      <h3 className="text-base sm:text-lg lg:text-2xl font-bold text-white flex items-center gap-2">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        Next Steps To Work With Me
                      </h3>
                      
                      <p className="text-[13px] sm:text-sm text-gray-400/90">
                        Let's work together and create something amazing.
                      </p>
                    </div>
                    
                    {/* Two Paths */}
                    <div className="relative grid md:grid-cols-2 gap-6 md:gap-12 pt-5 sm:pt-6">
                      {/* Artist Path */}
                      <div className="space-y-2">
                        <p className="text-[11px] sm:text-xs font-normal tracking-wide text-gray-400/85">
                          If you're an artist:
                        </p>
                        <Button className="w-full bg-[hsl(var(--accent-a))] text-white border border-transparent px-6 h-12 text-[15px] font-medium rounded-xl transition-all duration-300 hover:scale-[1.02] hover:brightness-110 focus-visible:ring-2 ring-[hsl(var(--accent-a))]/50 inline-flex items-center justify-center gap-2 leading-none box-border group" asChild>
                          <a href={applyUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                            Apply Here
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </a>
                        </Button>
                      </div>

                      {/* Centered Divider - Desktop Only */}
                      <div className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-gray-800/50" />

                      {/* Industry Path */}
                      <div className="space-y-2">
                        <p className="text-[11px] sm:text-xs font-normal tracking-wide text-gray-400/85">
                          If you're industry:
                        </p>
                        <Button className="w-full bg-transparent text-white px-6 h-12 text-[15px] font-medium rounded-xl transition-all duration-300 border border-[hsl(var(--accent-a))] hover:scale-[1.02] hover:border-[hsl(var(--accent-a))]/80 hover:bg-[hsl(var(--accent-a))]/10 focus-visible:ring-2 ring-[hsl(var(--accent-a))]/50 inline-flex items-center justify-center gap-2 leading-none box-border group" asChild>
                          <a href="mailto:hello@dk-mba.com" className="flex items-center justify-center gap-2">
                            Email Here
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SimpleReveal>

            {/* Right Column - Image - Desktop Only */}
            <SimpleReveal delay={180} className="hidden lg:block lg:row-start-1 lg:col-start-2">
              <img src={founderImage} alt="Damian Keyes" className="w-full max-w-lg mx-auto rounded-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl" />
            </SimpleReveal>
          </div>
        </div>
      </section>

      {/* Education Section - LIGHT */}


      <section id="academy" data-nav-theme="light" className="relative py-8 lg:py-16 bg-hero-human max-md:bg-gray-50 overflow-visible isolate">
        <AmbientLines variant="b" />
        <SectionSeam label="Education" />
        
        {/* Subtle Grid Overlay */}
        <div className="absolute inset-0 opacity-[0.015] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgb(0 0 0 / 0.15) 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Two Column Grid */}
          <div className="relative">
          <div className="grid lg:grid-cols-[1.1fr,1fr] lg:grid-rows-[auto_auto] gap-6 lg:gap-10 items-start">
            {/* Left-Aligned Header - Row 1, Col 1 */}
            <div className="lg:row-start-1 lg:col-start-1 mb-4 lg:mb-6">
              <SimpleReveal>
                <AnimatedBorderBadge text="The Education" className="mb-5" showPulsingDot={true} surfaceClassName="bg-hero-human max-md:bg-gray-50" />
              </SimpleReveal>
              
              <SimpleReveal delay={60}>
                <div className="space-y-1">
                  <div>
                    <h2 className="text-[clamp(1.25rem,3vw,1.75rem)] lg:text-[clamp(1.5rem,3.5vw,2.25rem)] font-medium text-gray-900 tracking-[-0.05em] leading-tight">
                      The Roadmap To
                    </h2>
                    <h2 className="text-[clamp(2rem,4.5vw,3.5rem)] lg:text-[clamp(2.5rem,5vw,4rem)] font-bold text-[hsl(var(--accent-a))] tracking-[-0.05em] -mt-1 leading-tight">
                      One Million Streams
                    </h2>
                  </div>
                  
                  <p className="text-[1.0625rem] lg:text-[1.125rem] text-gray-600 max-w-2xl">
                    The step-by-step system to grow real fans and build sustainable streaming momentum. The only course developed by a <span className="border-b-2 border-[hsl(var(--accent-a))] font-medium">real-life University Founder</span>.
                  </p>
                </div>
              </SimpleReveal>
            </div>

            {/* Academy Mockup - Mobile Only (appears after intro, before bullets) */}
            <SimpleReveal delay={200} className="lg:hidden">
              <div className="space-y-4">
                <div className="relative group" style={{
                  perspective: '2500px',
                  transformStyle: 'preserve-3d'
                }}>
                  <img src={academyMockup} alt="DK Music Business Academy platform preview" className="w-full h-auto transition-all duration-700 group-hover:scale-[1.02]" loading="lazy" style={{
                    transform: 'rotateX(2deg) rotateY(-2deg)',
                    transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.15))'
                  }} />
                </div>
                
                {/* Stat Bar */}
                <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm font-medium text-gray-600 px-4">
                  <div className="flex items-center gap-1.5">
                    <span className="flex h-2 w-2 relative">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--accent-a))] opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(var(--accent-a))]" />
                    </span>
                    <span className="whitespace-nowrap">30,000+ Active Students</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-[hsl(var(--accent-a))]" />
                    <span className="whitespace-nowrap">Proven Framework</span>
                  </div>
                </div>
              </div>
            </SimpleReveal>

            {/* Left Column - Expanded Copy - Row 2, Col 1 */}
            <div className="space-y-3 pt-2 lg:row-start-2 lg:col-start-1">
              <div className="space-y-3">
                <SimpleReveal delay={0}>
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[hsl(var(--accent-a))] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-[1.0625rem]">Spotify & Release Growth Courses</p>
                      <p className="text-gray-600 text-[0.9375rem] mt-1">Master the systems that drive thousands of independent artists past 1M streams.</p>
                    </div>
                  </div>
                </SimpleReveal>
                
                <SimpleReveal delay={100}>
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[hsl(var(--accent-a))] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-[1.0625rem]">Live Weekly Coaching</p>
                      <p className="text-gray-600 text-[0.9375rem] mt-1">Ask questions and get direct guidance from industry experts.</p>
                    </div>
                  </div>
                </SimpleReveal>
                
                <SimpleReveal delay={200}>
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[hsl(var(--accent-a))] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-[1.0625rem]">Real Artist Case Studies</p>
                      <p className="text-gray-600 text-[0.9375rem] mt-1">Learn from artists who've built sustainable, full-time music careers.</p>
                    </div>
                  </div>
                </SimpleReveal>
                
                <SimpleReveal delay={300}>
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[hsl(var(--accent-a))] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-[1.0625rem]">Templates, Tools & Checklists</p>
                      <p className="text-gray-600 text-[0.9375rem] mt-1">Everything you need to plan and execute successful releases.</p>
                    </div>
                  </div>
                </SimpleReveal>
                
                <SimpleReveal delay={400}>
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[hsl(var(--accent-a))] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-[1.0625rem]">Global Artist Community</p>
                      <p className="text-gray-600 text-[0.9375rem] mt-1">Join over 30,000 artists in 73+ countries building momentum together.</p>
                    </div>
                  </div>
                </SimpleReveal>
              </div>
              
              {/* CTA Button */}
              <SimpleReveal delay={500}>
                <div className="pt-5">
                  <a href={academyTrialUrl} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center gap-2 w-full h-12 bg-black hover:bg-gray-900 text-white text-[15px] font-medium rounded-lg hover:scale-[1.02] transition-all duration-300 focus-visible:ring-2 ring-black/50">
                    Start Your Roadmap
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </a>
                </div>
              </SimpleReveal>
            </div>

            <div className="hidden lg:block relative lg:order-none lg:row-start-1 lg:row-span-2 lg:col-start-2 lg:sticky lg:top-48 lg:self-start">
              {/* Glow Layer Behind - Outside sticky wrapper */}
              <div aria-hidden="true" className="absolute inset-0 -z-10 pointer-events-none" style={{
                background: 'transparent'
              }} />

              {/* Sticky Container - No transforms/filters here */}
              <div className="space-y-4">
                  {/* 3D Transform Layer with Shadow */}
                  <div className="relative group" style={{
                  transform: 'rotateX(2deg) rotateY(-3deg)',
                  transformStyle: 'preserve-3d',
                  perspective: '2000px',
                  backfaceVisibility: 'hidden',
                  willChange: 'transform'
                }}>
                    {/* Image */}
                    <img src={academyMockup} alt="DK Music Business Academy Platform" className="w-full h-auto transition-all duration-500 group-hover:brightness-105" loading="lazy" style={{
                    filter: 'none'
                  }} />
                  </div>

                <SimpleReveal delay={260}>
                  {/* Stat Bar */}
                  <div className="flex items-center justify-center gap-4 text-[0.8125rem] text-gray-600 pt-2">
                    <span>30,000+ Artists</span>
                    <span className="text-gray-500">•</span>
                    <span>73+ Countries</span>
                    <span className="text-gray-500">•</span>
                    <span>100M+ Streams Generated</span>
                  </div>
                </SimpleReveal>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Agency Section - LIGHT */}
      <section data-nav-theme="light" className="relative isolate py-8 lg:py-16 bg-hero-human max-md:bg-white">
        <AmbientLines />
        <SectionSeam label="Agency" />
        <div className="max-w-7xl mx-auto px-6">
          {/* Two Column Grid */}
          <div className="grid lg:grid-cols-[1fr,1.1fr] lg:grid-rows-[auto_auto] gap-6 lg:gap-10 items-start">
            {/* Left Column - Sticky Dual-Phone Container + Quote - sticky on desktop */}
            <div className="hidden lg:block lg:row-span-2 lg:self-stretch">
              <div className="lg:sticky lg:top-4">
                <SpotifyPhoneMockup />
                
                <div className="text-center mt-6 space-y-2">
                  <p className="text-base lg:text-lg text-gray-900 font-semibold">
                    You're just one song away
                  </p>
                  <p className="text-sm text-gray-500">
                    Real results from an artist we worked with
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Text Content */}
            <div className="space-y-6">
              {/* Badge + Headline + Paragraph Group */}
              <div className="mb-4 lg:mb-6">
                {/* Badge */}
                <SimpleReveal>
                  <AnimatedBorderBadge text="The Agency" className="mb-5 inline-block" showPulsingDot={true} surfaceClassName="bg-hero-human max-md:bg-white" />
                </SimpleReveal>
                
                {/* Two-line Headline + Body Paragraph */}
                <SimpleReveal delay={60}>
                  <div className="space-y-1">
                    <div>
                      <h2 className="text-[clamp(1.25rem,3vw,1.75rem)] lg:text-[clamp(1.5rem,3.5vw,2.25rem)] font-medium text-gray-900 tracking-[-0.05em] leading-tight">
                        Welcome To
                      </h2>
                      <h2 className="text-[clamp(2rem,4.5vw,3.5rem)] lg:text-[clamp(2.5rem,5vw,4rem)] font-bold text-[hsl(var(--accent-a))] tracking-[-0.05em] -mt-1 leading-tight">
                        The Artist Engine
                      </h2>
                    </div>
                    <p className="text-[1.0625rem] lg:text-[1.125rem] text-gray-600 max-w-2xl">
                      A proven system for artists ready to scale. We work closely with you to develop every part of your artist career: from refining your creative direction to executing release strategies that build real, lasting growth. We build <span className="border-b-2 border-[hsl(var(--accent-a))] font-medium">your career,</span> <span className="border-b-2 border-[hsl(var(--accent-a))] font-medium">together.</span>
                    </p>
                  </div>
                </SimpleReveal>
              </div>

              {/* Mobile Phone Mockup */}
              <SimpleReveal delay={200} className="lg:hidden">
                <div className="max-w-[260px] mx-auto">
                  <SpotifyPhoneMockup />
                  
                  <div className="text-center mt-6 space-y-2">
                    <p className="text-base lg:text-lg text-gray-900 font-semibold">
                      You're just one song away
                    </p>
                    <p className="text-sm text-gray-500">
                      real results from an artist we worked with
                    </p>
                  </div>
                </div>
              </SimpleReveal>

              {/* Bullet List */}
              <div className="space-y-3 pt-2">
                <SimpleReveal delay={0}>
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[hsl(var(--accent-a))] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-[1.0625rem]">Brand & Creative Development</p>
                      <p className="text-gray-600 text-[0.9375rem] mt-1">Shape your artist identity, visuals, and messaging so your music stands out and connects authentically.</p>
                    </div>
                  </div>
                </SimpleReveal>
                
                <SimpleReveal delay={100}>
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[hsl(var(--accent-a))] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-[1.0625rem]">Social Media Strategy & Growth</p>
                      <p className="text-gray-600 text-[0.9375rem] mt-1">Build a powerful online presence that drives engagement, visibility, and fan connection across platforms.</p>
                    </div>
                  </div>
                </SimpleReveal>
                
                <SimpleReveal delay={200}>
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[hsl(var(--accent-a))] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-[1.0625rem]">Content & Campaign Execution</p>
                      <p className="text-gray-600 text-[0.9375rem] mt-1">From release planning to ad strategy — we help you launch with clarity and impact.</p>
                    </div>
                  </div>
                </SimpleReveal>
                
                <SimpleReveal delay={300}>
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[hsl(var(--accent-a))] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-[1.0625rem]">Audience & Fanbase Growth</p>
                      <p className="text-gray-600 text-[0.9375rem] mt-1">Turn casual listeners into loyal fans with data-driven systems that build momentum over time.</p>
                    </div>
                  </div>
                </SimpleReveal>
                
                <SimpleReveal delay={400}>
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[hsl(var(--accent-a))] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-[1.0625rem]">Career Strategy & Industry Support</p>
                      <p className="text-gray-600 text-[0.9375rem] mt-1">1-1 guidance, tools, and expert insight to scale your career and sustain your success.</p>
                    </div>
                  </div>
                </SimpleReveal>
              </div>
              
              {/* CTA Button */}
              <SimpleReveal delay={500}>
                <div className="pt-5">
                  <a href={agencyLearnMoreUrl} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center gap-2 w-full h-12 bg-black hover:bg-gray-900 text-white text-[15px] font-medium rounded-lg hover:scale-[1.02] transition-all duration-300 focus-visible:ring-2 ring-black/50">
                    Explore The Artist Engine
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </a>
                </div>
              </SimpleReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Free Downloads Section - Cinema Noir Glass */}
      <section id="free-stuff" data-nav-theme="dark" className="py-8 lg:py-16 bg-[hsl(0_0%_2%)] relative overflow-hidden">
        <SectionSeam label="03 Free Stuff" dark />
        {/* SVG Filters for Dual-Layer Film Grain */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-0">
          <defs>
            {/* Fine grain layer */}
            <filter id="filmGrainFine">
              <feTurbulence type="fractalNoise" baseFrequency="1.0" numOctaves="5" stitchTiles="stitch" />
              <feColorMatrix type="saturate" values="0" />
            </filter>
            {/* Coarse grain layer */}
            <filter id="filmGrainCoarse">
              <feTurbulence type="fractalNoise" baseFrequency="0.5" numOctaves="3" stitchTiles="stitch" />
              <feColorMatrix type="saturate" values="0" />
            </filter>
          </defs>
        </svg>
        
        {/* 1. Radial Gradient Overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(0_0%_8%)_0%,hsl(0_0%_2%)_100%)] opacity-90" />
        
        {/* 2. Vignette Effect */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,hsl(0_0%_0%)_100%)] opacity-40" />
        
        {/* 3. Grid Pattern Overlay - Enhanced */}
        <div className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: `
              linear-gradient(rgba(255,255,255,0.25) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.25) 1px, transparent 1px)
            `,
        backgroundSize: '50px 50px',
        maskImage: 'radial-gradient(ellipse at center, black 0%, transparent 80%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 0%, transparent 80%)'
      }} />
        
        {/* 4. Fine Film Grain Overlay */}
        <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay pointer-events-none" style={{
        filter: 'url(#filmGrainFine)'
      }} />
        
        {/* 5. Coarse Film Grain Overlay */}
        <div className="absolute inset-0 opacity-[0.045] mix-blend-soft-light pointer-events-none" style={{
        filter: 'url(#filmGrainCoarse)'
      }} />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
        <SimpleReveal delay={0}>
          <div className="inline-block mb-5">
            <div className="badge-glow-blue bg-black border border-white/10 rounded-full px-5 py-2 inline-flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" style={{
                  animation: 'pulse-green-enhanced 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }} />
              <style>{`
                @keyframes pulse-green-enhanced {
                  0%, 100% {
                    opacity: 1;
                    transform: scale(1);
                    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
                  }
                  50% {
                    opacity: 0.4;
                    transform: scale(1.3);
                    box-shadow: 0 0 8px 4px rgba(34, 197, 94, 0);
                  }
                }
                
                @keyframes badge-glow-blue {
                  0%, 100% {
                    box-shadow: 0 0 0 0 rgba(0, 113, 227, 0);
                  }
                  50% {
                    box-shadow: 0 0 30px 4px rgba(0, 113, 227, 0.6),
                                0 0 50px 8px rgba(0, 113, 227, 0.3);
                  }
                }
                
                .badge-glow-blue {
                  animation: badge-glow-blue 2s ease-in-out infinite;
                }
              `}</style>
              <span className="text-sm font-normal text-white tracking-wide">
                Free Resources
              </span>
            </div>
          </div>
        </SimpleReveal>
          <SimpleReveal delay={100}>
            <div className="space-y-1">
              <h2 className="font-headline font-bold text-[hsl(var(--text))] text-[clamp(2rem,4.5vw,3.5rem)] lg:text-[clamp(2.5rem,5vw,4rem)] leading-[1.05] tracking-[-0.05em]">
                The Free Stuff
              </h2>
              <p className="text-[1.0625rem] lg:text-[1.125rem] text-[hsl(var(--text-muted))] leading-[1.55] max-w-3xl mx-auto">
                <span className="font-semibold">Free Downloads</span> to Actually Help You Grow. No Fluff. No Catch.
              </p>
            </div>
          </SimpleReveal>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-stretch">
          {/* Card 1 */}
          <SimpleReveal delay={300}>
              <div className="h-full flex flex-col backdrop-blur-xl bg-gradient-to-br from-black/[0.4] to-black/[0.6] border border-white/12 rounded-xl p-6 shadow-[0_8px_32px_0_rgba(0,0,0,0.5)] transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.7),0_0_40px_-10px_rgba(59,130,246,0.2)]">
                <div className="relative aspect-video bg-black/40 backdrop-blur-sm ring-1 ring-inset ring-white/10 rounded-xl overflow-hidden">
                  <img src={releasePlanImage} alt="20 Day Single Release Plan 2026 — pre and post release strategy" className="w-full h-full object-cover object-top" loading="lazy" />
                  <span className="absolute top-3 right-3 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide bg-emerald-500 text-white rounded-md shadow-lg">NEW</span>
                </div>
                <div className="flex-1 flex flex-col justify-between text-center space-y-5 mt-6">
                  <h3 className="text-xl font-bold text-[hsl(var(--text))]">
                    The 20 Day Single Release Plan
                  </h3>
                  <p className="text-[hsl(var(--text-muted))] text-sm leading-relaxed">
                    Release plans downloaded over 100k times, the 2026 Single Release Plan is everything you need to fully release your single successfully.
                  </p>
                  <p className="text-[hsl(var(--text-muted))] text-sm">
                    Includes pre and post release.
                  </p>
                  <Button className="w-full bg-[hsl(var(--accent-a))] hover:brightness-110 text-white h-12 text-[15px] font-medium rounded-lg transition-all duration-300 hover:scale-[1.02] group" asChild>
                    <a href="https://www.dk-mba.com/singlereleaseplan" target="_blank" rel="noopener noreferrer">
                      Download Now
                      <ArrowRight className="ml-2 h-4 w-4 inline-block group-hover:translate-x-0.5 transition-transform duration-300" />
                    </a>
                  </Button>
                </div>
              </div>
            </SimpleReveal>

          {/* Card 2 */}
          <SimpleReveal delay={450}>
              <div className="h-full flex flex-col backdrop-blur-xl bg-gradient-to-br from-black/[0.4] to-black/[0.6] border border-white/12 rounded-xl p-6 shadow-[0_8px_32px_0_rgba(0,0,0,0.5)] transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_20px_40px_-15px_rgba(59,130,246,0.2)]">
                <div className="aspect-video bg-black/40 backdrop-blur-sm ring-1 ring-inset ring-white/10 rounded-xl overflow-hidden">
                  <img src={hooksImage} alt="100 Hooks Blueprint showing Turn 200 Views Into 200K Views with phone mockups" className="w-full h-full object-cover object-[center_15%]" loading="lazy" />
                </div>
                <div className="flex-1 flex flex-col justify-between text-center space-y-5 mt-6">
                  <h3 className="text-xl font-bold text-[hsl(var(--text))]">
                    The 100 Hooks Blueprint
                  </h3>
                  <p className="text-[hsl(var(--text-muted))] text-sm leading-relaxed">
                    100+ Hooks developed by my social team proven to get millions of views with artists we work with.
                  </p>
                  <p className="text-[hsl(var(--text-muted))] text-sm">
                    Developed for Instagram, TikTok and YouTube Shorts
                  </p>
                  <Button className="w-full bg-[hsl(var(--accent-a))] hover:brightness-110 text-white h-12 text-[15px] font-medium rounded-lg transition-all duration-300 hover:scale-[1.02] group" asChild>
                    <a href="https://www.dk-mba.com/viralhooks" target="_blank" rel="noopener noreferrer">
                      Download Now
                      <ArrowRight className="ml-2 h-4 w-4 inline-block group-hover:translate-x-0.5 transition-transform duration-300" />
                    </a>
                  </Button>
                </div>
              </div>
            </SimpleReveal>

          {/* Card 3 */}
          <SimpleReveal delay={600}>
              <div className="h-full flex flex-col backdrop-blur-xl bg-gradient-to-br from-black/[0.4] to-black/[0.6] border border-white/12 rounded-xl p-6 shadow-[0_8px_32px_0_rgba(0,0,0,0.5)] transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.7),0_0_40px_-10px_rgba(59,130,246,0.2)] flex">
                <div className="aspect-video bg-black/40 backdrop-blur-sm ring-1 ring-inset ring-white/10 rounded-xl overflow-hidden">
                  <img src={contentIdeasImage} alt="365 Content Ideas for Musicians cover" className="w-full h-full object-cover object-top" loading="lazy" />
                </div>
                <div className="flex-1 flex flex-col justify-between text-center space-y-5 mt-6">
                  <h3 className="text-xl font-bold text-[hsl(var(--text))]">
                    365 Content Ideas
                  </h3>
                  <p className="text-[hsl(var(--text-muted))] text-sm leading-relaxed">
                    365 filmable, step-by-step content ideas for TikTok and Instagram — each one tells you exactly what to shoot, how to make it and what to ask for.
                  </p>
                  <p className="text-[hsl(var(--text-muted))] text-sm">
                    Includes best timing and a reference video that already works.
                  </p>
                  <Button className="w-full bg-[hsl(var(--accent-a))] hover:brightness-110 text-white h-12 text-[15px] font-medium rounded-lg transition-all duration-300 hover:scale-[1.02] group" asChild>
                    <a href="https://www.dk-mba.com/365contentideas" target="_blank" rel="noopener noreferrer">
                      Download Now
                      <ArrowRight className="ml-2 h-4 w-4 inline-block group-hover:translate-x-0.5 transition-transform duration-300" />
                    </a>
                  </Button>
                </div>
              </div>
            </SimpleReveal>
          </div>
        </div>
      </section>

      {/* Final CTA Section - DARK */}
      <section id="apply" data-nav-theme="dark" className="py-8 lg:py-16 bg-[hsl(0_0%_2%)] relative overflow-hidden">
        <SectionSeam label="04 Apply" dark />
        {/* SVG Filters for Dual-Layer Film Grain */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-0">
          <defs>
            {/* Fine grain layer */}
            <filter id="filmGrainFineFooter">
              <feTurbulence type="fractalNoise" baseFrequency="1.0" numOctaves="5" stitchTiles="stitch" />
              <feColorMatrix type="saturate" values="0" />
            </filter>
            {/* Coarse grain layer */}
            <filter id="filmGrainCoarseFooter">
              <feTurbulence type="fractalNoise" baseFrequency="0.5" numOctaves="3" stitchTiles="stitch" />
              <feColorMatrix type="saturate" values="0" />
            </filter>
          </defs>
        </svg>
        
        {/* 1. Radial Gradient Overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(0_0%_8%)_0%,hsl(0_0%_2%)_100%)] opacity-90" />
        
        {/* 2. Vignette Effect */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,hsl(0_0%_0%)_100%)] opacity-40" />
        
        {/* 3. Grid Pattern Overlay - Enhanced */}
        <div className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: `
            linear-gradient(rgba(255,255,255,0.25) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.25) 1px, transparent 1px)
          `,
        backgroundSize: '50px 50px',
        maskImage: 'radial-gradient(ellipse at center, black 0%, transparent 80%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 0%, transparent 80%)'
      }} />
        
        {/* 4. Fine Film Grain Overlay */}
        <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay pointer-events-none" style={{
        filter: 'url(#filmGrainFineFooter)'
      }} />
        
        {/* 5. Coarse Film Grain Overlay */}
        <div className="absolute inset-0 opacity-[0.045] mix-blend-soft-light pointer-events-none" style={{
        filter: 'url(#filmGrainCoarseFooter)'
      }} />

        {/* Smooth transition fade from section above */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent via-[hsl(0_0%_2%)]/50 to-[hsl(0_0%_2%)] pointer-events-none -mt-32 z-20" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Featured Quote Section */}
          <div className="text-center max-w-5xl mx-auto">
            {/* Badge */}
            <SimpleReveal delay={0}>
              <div className="inline-block mb-5">
                <div className="badge-glow-blue bg-black border border-white/10 rounded-full px-5 py-2 inline-flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" style={{
                  animation: 'pulse-green-enhanced 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }} />
                  <style>{`
                    @keyframes pulse-green-enhanced {
                      0%, 100% {
                        opacity: 1;
                        transform: scale(1);
                        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
                      }
                      50% {
                        opacity: 0.4;
                        transform: scale(1.3);
                        box-shadow: 0 0 8px 4px rgba(34, 197, 94, 0);
                      }
                    }
                    
                    @keyframes badge-glow-blue {
                      0%, 100% {
                        box-shadow: 0 0 0 0 rgba(0, 113, 227, 0);
                      }
                      50% {
                        box-shadow: 0 0 30px 4px rgba(0, 113, 227, 0.6),
                                    0 0 50px 8px rgba(0, 113, 227, 0.3);
                      }
                    }
                    
                    .badge-glow-blue {
                      animation: badge-glow-blue 2s ease-in-out infinite;
                    }
                  `}</style>
                  <span className="text-sm font-normal text-white tracking-wide">
                    Next Steps
                  </span>
                </div>
              </div>
            </SimpleReveal>

            {/* Featured Quote - Hero Size */}
            <SimpleReveal delay={100}>
              <h2 className="text-[clamp(2rem,4.5vw,3.5rem)] lg:text-[clamp(2.5rem,5vw,4rem)] font-bold tracking-[-0.05em] text-white leading-[1.1] mb-8">
                You're just one song away...
              </h2>
            </SimpleReveal>

            {/* Supporting Subtext - Combines remaining messages */}
            <SimpleReveal delay={200}>
              <p className="text-[1.125rem] lg:text-[1.25rem] text-gray-400 leading-[1.6] max-w-3xl mx-auto mb-12 lg:mb-16">
                Not sure where to start? You're closer than you think. Momentum starts with one decision. Let's figure out your next move together.
              </p>
            </SimpleReveal>
          </div>

          {/* Growth Chart */}
          <SimpleReveal delay={300}>
            <div className="max-w-4xl mx-auto mb-12 lg:mb-16">
              <Suspense fallback={<div className="h-80 flex items-center justify-center">
                  <div className="text-[hsl(var(--text-muted))]">Loading chart...</div>
                </div>}>
                <GrowthProjectionChart />
              </Suspense>
            </div>
          </SimpleReveal>

          {/* Paragraph */}
          <SimpleReveal delay={400}>
            <p className="text-[1.0625rem] lg:text-[1.125rem] text-[hsl(var(--text-muted))] leading-[1.55] text-center max-w-3xl mx-auto mb-12 lg:mb-16">
              Whether you're releasing your first single or looking to take the next big step in your career, we're here to help you build a fanbase that lasts.
            </p>
          </SimpleReveal>

          {/* CTA Buttons */}
          <SimpleReveal delay={500}>
            <div className="max-w-2xl mx-auto">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Left - 7 Day Free Trial */}
                <a href={academyTrialUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-center gap-2 h-12 w-full bg-[hsl(var(--accent-a))] hover:brightness-110 text-white rounded-lg text-[15px] font-medium transition-all duration-300 hover:scale-[1.02] focus-visible:ring-2 ring-[hsl(var(--accent-a))]/50">
                  Join The Academy
                  <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>

                {/* Right - Apply Here */}
                <a href={applyUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-center gap-2 h-12 w-full bg-transparent hover:border-[hsl(var(--accent-a))]/80 text-white border border-[hsl(var(--accent-a))] rounded-lg text-[15px] font-medium transition-all duration-300 hover:scale-[1.02] focus-visible:ring-2 ring-[hsl(var(--accent-a))]/50">
                  Apply To Work Together
                  <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          </SimpleReveal>
        </div>
      </section>

      {/* Footer */}
      <Suspense fallback={<div className="h-20 bg-gray-900" />}>
        <Footer applyUrl={applyUrl} />
      </Suspense>
    </div>;
}